import React from 'react';
import classnames from 'classnames';
import type {IconName, IconPrefix} from '@fortawesome/fontawesome-common-types';

import {Icon} from '@albert-io/atomic';

interface IllustrationProps {
  icon: IconName | [IconPrefix, IconName];
  /** Icon badge colors (e.g. 'background-clementine-subtle content-clementine'); brand by default. */
  badgeClassName?: string;
}

// A ghost list row: icon badge + two skeleton bars, mirroring a real row.
const SkeletonCard = ({icon, badgeClassName}: IllustrationProps) => (
  <div className='gap-x1-half rounded-x1-half border-subtle background-primary p-x1 flex w-[232px] items-center border'>
    <div
      className={classnames(
        'flex h-x4-half w-x4-half shrink-0 items-center justify-center rounded-full',
        badgeClassName ?? 'background-brand-subtle-hover'
      )}
    >
      <Icon
        className='text-s'
        color={badgeClassName ? 'inherit' : 'brand'}
        icon={icon}
        iconStyle='regular'
      />
    </div>
    <div className='gap-x0-half flex grow flex-col'>
      <div className='h-x1 background-bold w-[100px] rounded-full' />
      <div className='h-x1 w-x5 background-bold rounded-full' />
    </div>
  </div>
);

/** Two overlapping ghost rows — the decorative illustration above empty-state copy. */
export const EmptyIllustration = ({icon, badgeClassName}: IllustrationProps) => (
  <div aria-hidden='true' className='grid place-items-start'>
    <div className='col-start-1 row-start-1'>
      <SkeletonCard badgeClassName={badgeClassName} icon={icon} />
    </div>
    <div className='mt-x4 ml-x4 rounded-x1-half elevation-shadow-l2 col-start-1 row-start-1'>
      <SkeletonCard badgeClassName={badgeClassName} icon={icon} />
    </div>
  </div>
);

interface Props {
  illustration: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Centered dashboard empty state: a decorative illustration, copy (children), and an optional
 * action. Shared by the Assignments and People (co-teachers) empty states.
 */
export const EmptyState = ({illustration, action, children}: Props) => (
  <div className='gap-x3 py-x8 flex w-full flex-col items-center text-center'>
    {illustration}
    {children}
    {action}
  </div>
);
